import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { PeriodPicker } from "@/components/PeriodPicker";
import { StatCard } from "@/components/StatCard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ToggleGroup, ToggleGroupItem,
} from "@/components/ui/toggle-group";
import {
  Bar, BarChart, CartesianGrid, Legend, Line, ComposedChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  analyticsByVendor,
  varianceByCategory, varianceCumulative, varianceMonthly,
} from "@/lib/api";
import { EUR, num, paymentMethodOptions } from "@/lib/format";
import { ExportButtons } from "@/components/ExportButtons";
import { buildFilename } from "@/lib/export";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function Variance() {
  const thisYear = new Date().getFullYear();
  const [basis, setBasis] = useState("accrual"); // default accrual for managerial analysis
  const [dateFrom, setDateFrom] = useState(`${thisYear}-01-01`);
  const [dateTo, setDateTo] = useState(`${thisYear}-12-31`);
  const [method, setMethod] = useState("");
  const [monthly, setMonthly] = useState([]);
  const [cumulative, setCumulative] = useState([]);
  const [byCategory, setByCategory] = useState([]);
  const [byVendor, setByVendor] = useState([]);

  useEffect(() => {
    (async () => {
      const params = { date_from: dateFrom, date_to: dateTo, basis };
      if (method) params.method = method;
      const [m, c, k, v] = await Promise.all([
        varianceMonthly(params),
        varianceCumulative(params),
        varianceByCategory(params),
        analyticsByVendor(params),
      ]);
      setMonthly(m || []);
      setCumulative(c || []);
      setByCategory(k || []);
      setByVendor(v || []);
    })();
  }, [basis, dateFrom, dateTo, method]);

  const totals = useMemo(() => {
    const planned = monthly.reduce((s, r) => s + num(r.planned), 0);
    const actual = monthly.reduce((s, r) => s + num(r.actual), 0);
    const variance = planned - actual;
    return { planned, actual, variance };
  }, [monthly]);

  const chartData = useMemo(
    () =>
      cumulative.map((r) => ({
        month: `${r.year}-${String(r.month).padStart(2, "0")}`,
        planned: num(r.planned),
        actual: num(r.actual),
        cumulative_variance: num(r.cumulative_variance),
      })),
    [cumulative],
  );

  return (
    <div data-testid="variance-page">
      <PageHeader
        title="Scostamento (pianificato vs effettivo)"
        subtitle="Confronto tra previsione e pagamenti reali"
        actions={
          <div className="flex flex-wrap items-center gap-3">
            <PeriodPicker
              value={{ from: dateFrom, to: dateTo }}
              onChange={(f, t) => {
                setDateFrom(f);
                setDateTo(t);
              }}
              testId="var-period"
              align="end"
            />
            <div className="flex items-center gap-1">
              <Label className="text-xs uppercase tracking-wider text-stone-500">
                Dal
              </Label>
              <Input
                type="date"
                data-testid="input-date-from"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-[150px]"
              />
            </div>
            <div className="flex items-center gap-1">
              <Label className="text-xs uppercase tracking-wider text-stone-500">
                Al
              </Label>
              <Input
                type="date"
                data-testid="input-date-to"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-[150px]"
              />
            </div>
            <ExportButtons
              sheets={[
                {
                  name: "Mensile",
                  rows: monthly.map((r) => ({
                    label: r.label,
                    planned: r.planned,
                    actual: r.actual,
                    variance: r.variance,
                    variance_pct: r.variance_pct,
                  })),
                  columns: [
                    { key: "label", header: "Mese" },
                    { key: "planned", header: "Pianificato (€)", type: "currency" },
                    {
                      key: "actual",
                      header:
                        basis === "cash"
                          ? "Pagato cassa (€)"
                          : "Pagato competenza (€)",
                      type: "currency",
                    },
                    { key: "variance", header: "Δ (€)", type: "currency" },
                    { key: "variance_pct", header: "Δ %", type: "percent" },
                  ],
                },
                {
                  name: "Per categoria",
                  rows: byCategory.map((r) => ({
                    label: r.label,
                    planned: r.planned,
                    actual: r.actual,
                    variance: r.variance,
                  })),
                  columns: [
                    { key: "label", header: "Categoria" },
                    { key: "planned", header: "Pianificato (€)", type: "currency" },
                    { key: "actual", header: "Pagato (€)", type: "currency" },
                    { key: "variance", header: "Δ (€)", type: "currency" },
                  ],
                },
                {
                  name: "Per fornitore",
                  rows: byVendor.map((r) => ({
                    vendor_name: r.vendor_name,
                    occurrences: r.occurrences,
                    payments: r.payments,
                    planned: r.planned,
                    actual: r.actual,
                    variance: r.variance,
                    variance_pct: r.variance_pct,
                  })),
                  columns: [
                    { key: "vendor_name", header: "Fornitore" },
                    { key: "occurrences", header: "Scadenze", type: "integer" },
                    { key: "payments", header: "Pagamenti", type: "integer" },
                    { key: "planned", header: "Pianificato (€)", type: "currency" },
                    { key: "actual", header: "Pagato (€)", type: "currency" },
                    { key: "variance", header: "Δ (€)", type: "currency" },
                    { key: "variance_pct", header: "Δ %", type: "percent" },
                  ],
                },
                {
                  name: "Cumulato",
                  rows: cumulative.map((r) => ({
                    period: `${r.year}-${String(r.month).padStart(2, "0")}`,
                    planned: r.planned,
                    actual: r.actual,
                    cumulative_variance: r.cumulative_variance,
                  })),
                  columns: [
                    { key: "period", header: "Periodo" },
                    { key: "planned", header: "Pianificato (€)", type: "currency" },
                    { key: "actual", header: "Pagato (€)", type: "currency" },
                    {
                      key: "cumulative_variance",
                      header: "Δ cumulato (€)",
                      type: "currency",
                    },
                  ],
                },
              ]}
              filenameBase={buildFilename(`scostamento_${basis}`, {
                date_from: dateFrom,
                date_to: dateTo,
              })}
              testId="export-variance"
            />
          </div>
        }
      />

      <div
        data-testid="basis-toggle"
        className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-stone-200 bg-white p-4"
      >
        <div className="max-w-3xl">
          <div className="text-sm font-semibold text-stone-900">Base di analisi</div>
          <ul className="mt-2 space-y-1 text-xs text-stone-600">
            <li>
              <strong>Cassa:</strong> pagamenti aggregati per <em>data di esecuzione</em>.
              Utile per il monitoraggio della liquidità — risponde a "quando è uscito il denaro?".
            </li>
            <li>
              <strong>Competenza:</strong> pagamenti allocati al <em>periodo della scadenza</em>.
              Utile per l'analisi economica — risponde a "a quale mese appartiene questa scadenza?".
              Allineata alla previsione.
            </li>
          </ul>
        </div>
        <div className="flex flex-col items-end gap-3">
          <ToggleGroup
            type="single"
            value={basis}
            onValueChange={(v) => v && setBasis(v)}
          >
            <ToggleGroupItem value="cash" data-testid="basis-cash">
              Cassa
            </ToggleGroupItem>
            <ToggleGroupItem value="accrual" data-testid="basis-accrual">
              Competenza
            </ToggleGroupItem>
          </ToggleGroup>
          <div className="flex items-center gap-2">
            <Label className="text-[11px] uppercase tracking-wider text-stone-500">
              Metodo
            </Label>
            <Select
              value={method || "all"}
              onValueChange={(v) => setMethod(v === "all" ? "" : v)}
            >
              <SelectTrigger
                data-testid="variance-method"
                className="h-9 w-[140px]"
              >
                <SelectValue placeholder="Tutti" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                {paymentMethodOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <StatCard testId="stat-planned" label="Pianificato periodo" value={totals.planned} />
        <StatCard
          testId="stat-actual"
          label={basis === "cash" ? "Pagato (cassa)" : "Pagato (competenza)"}
          value={totals.actual}
        />
        <StatCard
          testId="stat-variance"
          label="Scostamento"
          value={totals.variance}
          tone={totals.variance > 0 ? "warning" : totals.variance < 0 ? "negative" : "positive"}
          hint={totals.variance > 0 ? "sotto budget" : totals.variance < 0 ? "sopra budget" : "in linea"}
        />
      </div>

      <section className="mt-8 rounded-xl border border-stone-200 bg-white p-5">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-stone-600">
          Andamento (vista {basis === "cash" ? "cassa" : "competenza"})
        </h2>
        <div data-testid="variance-chart" className="h-80 w-full">
          <ResponsiveContainer width="100%" height="100%" minHeight={320}>
            <ComposedChart data={chartData} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
              <XAxis dataKey="month" fontSize={11} stroke="#78716C" />
              <YAxis fontSize={11} stroke="#78716C" tickFormatter={(v) => `€${v}`} />
              <Tooltip formatter={(v) => EUR(v)} contentStyle={{ fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="planned" name="Pianificato" fill="#2A4B3C" radius={[4, 4, 0, 0]} />
              <Bar dataKey="actual" name="Pagato" fill="#10B981" radius={[4, 4, 0, 0]} />
              <Line
                type="monotone"
                dataKey="cumulative_variance"
                name="Scostamento cumulato"
                stroke="#B45309"
                strokeWidth={2}
                dot
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-stone-200 bg-white">
          <div className="border-b border-stone-200 px-5 py-3 text-sm font-semibold uppercase tracking-wider text-stone-600">
            Scostamento mensile
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Mese</TableHead>
                <TableHead className="text-right">Pianificato</TableHead>
                <TableHead className="text-right">Pagato</TableHead>
                <TableHead className="text-right">Δ</TableHead>
                <TableHead className="text-right">%</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {monthly.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center text-sm text-stone-500">
                    Nessun dato nel periodo.
                  </TableCell>
                </TableRow>
              )}
              {monthly.map((r) => {
                const v = num(r.variance);
                return (
                  <TableRow key={r.group_key} data-testid={`variance-row-${r.group_key}`}>
                    <TableCell>{r.label}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {EUR(r.planned)}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {EUR(r.actual)}
                    </TableCell>
                    <TableCell
                      className={
                        "text-right font-mono tabular-nums " +
                        (v > 0
                          ? "text-amber-700"
                          : v < 0
                          ? "text-rose-700"
                          : "text-emerald-700")
                      }
                    >
                      {EUR(v)}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-stone-500">
                      {r.variance_pct !== null && r.variance_pct !== undefined
                        ? `${(r.variance_pct * 100).toFixed(1)}%`
                        : "—"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <div className="rounded-xl border border-stone-200 bg-white">
          <div className="border-b border-stone-200 px-5 py-3 text-sm font-semibold uppercase tracking-wider text-stone-600">
            Scostamento per categoria
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Categoria</TableHead>
                <TableHead className="text-right">Pianificato</TableHead>
                <TableHead className="text-right">Pagato</TableHead>
                <TableHead className="text-right">Δ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {byCategory.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="py-8 text-center text-sm text-stone-500">
                    Nessun dato.
                  </TableCell>
                </TableRow>
              )}
              {byCategory.map((r) => {
                const v = num(r.variance);
                return (
                  <TableRow key={r.group_key} data-testid={`cat-variance-${r.group_key}`}>
                    <TableCell>{r.label}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {EUR(r.planned)}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {EUR(r.actual)}
                    </TableCell>
                    <TableCell
                      className={
                        "text-right font-mono tabular-nums " +
                        (v > 0 ? "text-amber-700" : v < 0 ? "text-rose-700" : "text-emerald-700")
                      }
                    >
                      {EUR(v)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </section>

      <section className="mt-8 rounded-xl border border-stone-200 bg-white">
        <div className="border-b border-stone-200 px-5 py-3 text-sm font-semibold uppercase tracking-wider text-stone-600">
          Scostamento per fornitore
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Fornitore</TableHead>
              <TableHead className="text-right">Scadenze</TableHead>
              <TableHead className="text-right">Pagamenti</TableHead>
              <TableHead className="text-right">Pianificato</TableHead>
              <TableHead className="text-right">Pagato</TableHead>
              <TableHead className="text-right">Δ</TableHead>
              <TableHead className="text-right">%</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {byVendor.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center text-sm text-stone-500">
                  Nessun dato.
                </TableCell>
              </TableRow>
            )}
            {byVendor.map((r) => {
              const v = num(r.variance);
              return (
                <TableRow key={r.vendor_id || "none"} data-testid={`vendor-variance-${r.vendor_id || "none"}`}>
                  <TableCell className="font-medium">{r.vendor_name}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{r.occurrences}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{r.payments}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{EUR(r.planned)}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{EUR(r.actual)}</TableCell>
                  <TableCell
                    className={
                      "text-right font-mono tabular-nums " +
                      (v > 0 ? "text-amber-700" : v < 0 ? "text-rose-700" : "text-emerald-700")
                    }
                  >
                    {EUR(v)}
                  </TableCell>
                  <TableCell className="text-right font-mono tabular-nums text-stone-500">
                    {r.variance_pct !== null && r.variance_pct !== undefined
                      ? `${(r.variance_pct * 100).toFixed(1)}%`
                      : "—"}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </section>
    </div>
  );
}
